import * as THREE from 'three';
import { IViewerElement } from './ViewerElement';
import { Utilities } from '../Utilities';
import { LayerManager, LayerType } from '../ViewerScreen/LayerManager';

export class ViewerElementHandler {
  private elements: IViewerElement[] = [];
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private layerManager: LayerManager;
  public onElementClick: (id: string) => void;
  public elementsDisabled: boolean = false;
  private isTilingMode: boolean = false;

  constructor(
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    elements: IViewerElement[],
    onElementClick: (id: string) => void,
    layerManager: LayerManager,
    isTilingMode: boolean = false,
  ) {
    this.scene = scene;
    this.camera = camera;
    this.elements = elements;
    this.onElementClick = onElementClick;
    this.layerManager = layerManager;
    this.isTilingMode = isTilingMode;
    this.addElementsToScene();
  }

  public showElements(): void {
    this.elements?.forEach((element) => {
      element.element3dObject.visible = true;
    });
    this.elementsDisabled = false;
  }

  public hideElements(): void {
    this.elements?.forEach((element) => {
      element.element3dObject.visible = false;
    });
    this.elementsDisabled = true;
  }

  public filterElementsByStatus(status: string) {
    if (status === 'all') {
      this.showElements();
      return;
    }
    const filteredElements = this.elements.filter((element) => element.status === status);
    this.hideElements();
    filteredElements?.forEach((element) => (element.element3dObject.visible = true));
  }

  private addElementsToScene(): void {
    this.elements?.forEach((element) => {
      if (this.isTilingMode) {
        // In tiling mode, apply 90-degree clockwise rotation around Y-axis
        // The cube geometry coordinate system differs from sphere by this rotation
        // Transformation: (x, y, z) → (z, y, -x)
        const originalPos = element.element3dObject.position.clone();
        const newX = originalPos.z;
        const newY = originalPos.y;
        const newZ = -originalPos.x;
        element.element3dObject.position.set(newX, newY, newZ);
      }
      
      element.element3dObject.updateMatrixWorld(true);
      this.layerManager.addToLayer(LayerType.ELEMENTS, element.element3dObject);
    });
  }

  public removeElements(): void {
    this.elements?.forEach((element) => {
      this.layerManager.removeFromLayer(LayerType.ELEMENTS, element.element3dObject);
      Utilities.DisposeObject3D(this.scene, element.element3dObject);
    });
    this.elements = [];
  }

  public setElements(elements: IViewerElement[], onElementClick: (id: string) => void): void {
    if (this.elements.length > 0) this.removeElements();
    this.elements = elements;
    this.onElementClick = onElementClick;
    this.addElementsToScene();
  }

  public findElement(object: THREE.Object3D): IViewerElement | null {
    return (
      this.elements?.find(
        (element) =>
          element.id === object.name ||
          element.id === object.parent?.name ||
          element.id === object.parent?.parent?.name,
      ) || null
    );
  }

  public alignElementsToCamera(): void {
    this.elements?.forEach((element) => {
      element.element3dObject?.lookAt(this.camera.position);
      element.element3dObject?.updateMatrixWorld(true);
    });
  }

  /**
   * Update the tiling mode and adjust element positions accordingly
   */
  public setTilingMode(isTilingMode: boolean): void {
    if (this.isTilingMode === isTilingMode) {
      return; // No change needed
    }
    
    const wasInTilingMode = this.isTilingMode;
    this.isTilingMode = isTilingMode;
    
    // When switching modes, need to apply/undo the 90° rotation
    this.elements?.forEach((element) => {
      const pos = element.element3dObject.position;
      
      if (isTilingMode && !wasInTilingMode) {
        // Switching from sphere to tiling: apply 90° CW rotation around Y
        // (x, y, z) → (z, y, -x)
        const oldX = pos.x;
        pos.x = pos.z;
        pos.z = -oldX;
      } else if (!isTilingMode && wasInTilingMode) {
        // Switching from tiling to sphere: apply 90° CCW rotation around Y (inverse)
        // (x, y, z) → (-z, y, x)
        const oldX = pos.x;
        pos.x = -pos.z;
        pos.z = oldX;
      }
      
      element.element3dObject.updateMatrixWorld(true);
    });
  }
}
